import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import {
    TFormalOffer,
    TFormalOffers,
    TFormalOfferVariationTree,
    TFormalOfferVariationTrees
} from '@helpers/FormalOffers/formalOffers.types';
import { getIsRCFOffer } from '@helpers/FormalOffers/getIsRCFOffer';
import { isFormalOfferVariationType } from '@helpers/FormalOffers/isFormalOfferVariationType';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import orderBy from 'lodash/orderBy';
import { useIntl } from 'react-intl';

import { OfferChoiceButton } from './OfferChoiceButton';
import styles from './OfferChoiceButtons.module.css';

export const OfferChoiceButtons = ({
    selectedOffer,
    updateSelectedOffer,
    formalOffers,
    variationTrees,
    drawdownAmount,
    hasInterestOnlyChoice
}: {
    selectedOffer: TFormalOffer | null | undefined;
    updateSelectedOffer: (offer: TFormalOffer) => void;
    formalOffers: TFormalOffers;
    variationTrees: TFormalOfferVariationTrees;
    drawdownAmount: number;
    hasInterestOnlyChoice: boolean;
}) => {
    const intl = useIntl();

    const formalOffersWithoutRCF = formalOffers.filter(
        (offer) => !getIsRCFOffer(offer)
    );
    const buttonList = useButtonList({
        formalOffers: formalOffersWithoutRCF,
        variationTrees
    });

    if (buttonList.length === 1 && getCountry() === 'DE') {
        updateSelectedOffer(buttonList[0].offer);

        return null;
    }

    return (
        <>
            <p className="mt-2xl text-m font-bold">
                {hasInterestOnlyChoice
                    ? intl.formatMessage({
                          id: '_.interest_only.radio_buttons.heading',
                          defaultMessage:
                              'How would you like to repay your loan?'
                      })
                    : intl.formatMessage({
                          id: '_.duration_choice.radio_buttons.heading',
                          defaultMessage: 'I want to repay over a maximum of'
                      })}
            </p>
            <div className={styles.radioButtonContainer}>
                {buttonList.map(({ offer, toggles, pills }) => (
                    <OfferChoiceButton
                        key={offer.offer_id}
                        offer={offer}
                        selectedOffer={selectedOffer}
                        updateSelectedOffer={updateSelectedOffer}
                        drawdownAmount={drawdownAmount}
                        toggles={toggles}
                        pills={pills}
                    />
                ))}
            </div>
        </>
    );
};

type TButtonProps = Pick<
    Parameters<typeof OfferChoiceButton>[0],
    'offer' | 'toggles' | 'pills'
>;
function useButtonList({
    formalOffers,
    variationTrees
}: {
    formalOffers: TFormalOffers;
    variationTrees: TFormalOfferVariationTrees;
}): TButtonProps[] {
    const { branch: splitTestBranch } = useSplitTestBranch({
        test: 'new_customer_interest_only_add_on_test_2025_q2'
    });

    const formalOffersById: Record<string, (typeof formalOffers)[number]> = {};
    for (const formalOffer of formalOffers) {
        formalOffersById[formalOffer.formal_offer_id] = formalOffer;
    }

    const effectiveTrees: TFormalOfferVariationTrees = [];
    for (const tree of variationTrees) {
        if (tree.base_formal_offer_id in formalOffersById) {
            effectiveTrees.push(tree);
        } else {
            // In the case where the variation offers exist in the `formalOffers` list,
            // but the base offer doesn't (e.g., in DE where 15 month offers show up in a
            // separate list from the 12 month offers) then treat the variations as effectively
            // their own trees
            for (const variation of tree.variations) {
                if (variation.formal_offer_id in formalOffersById) {
                    effectiveTrees.push({
                        base_formal_offer_id: variation.formal_offer_id,
                        variations: []
                    });
                }
            }
        }
    }

    const getEffectiveVariations = (tree: TFormalOfferVariationTree) =>
        tree.variations.flatMap((variation) =>
            variation.formal_offer_id in formalOffersById &&
            isFormalOfferVariationType(variation.variation_type)
                ? [
                      {
                          offer: formalOffersById[variation.formal_offer_id]!,
                          variationType: variation.variation_type
                      }
                  ]
                : []
        );

    let buttonList: TButtonProps[];
    switch (splitTestBranch) {
        case 'LEGACY':
        case 'TOGGLE':
            buttonList = effectiveTrees.map((tree) => ({
                offer: formalOffersById[tree.base_formal_offer_id]!,
                toggles: getEffectiveVariations(tree),
                pills: getEffectiveVariations(tree)
            }));
            break;
        case 'INTERSTITIAL_PAGE':
            buttonList = effectiveTrees.map((tree) => ({
                offer: formalOffersById[tree.base_formal_offer_id]!,
                toggles: [],
                pills: getEffectiveVariations(tree)
            }));
            break;
        default:
            buttonList = effectiveTrees.flatMap((tree) => [
                {
                    offer: formalOffersById[tree.base_formal_offer_id]!,
                    toggles: [],
                    pills: []
                },
                ...tree.variations.flatMap((variation) =>
                    variation.formal_offer_id in formalOffersById
                        ? [
                              {
                                  offer: formalOffersById[
                                      variation.formal_offer_id
                                  ],
                                  toggles: [],
                                  pills: []
                              }
                          ]
                        : []
                )
            ]);
    }

    return orderBy(buttonList, ['offer.duration.amount'], 'asc');
}
